/**
 * Curated glossary of recurring domain terms the chat OVERVIEW digest talks about; single source of
 * truth, surfaced as on-demand tooltips behind the first mention of each term (see {@link ./remarkGlossary}).
 *
 * Definitions are intentionally ONE LINE each and are sourced from how the backend digest/system prompt
 * (`ChatController.DOMAIN_PRIMER` — "WHAT THE DATA MEANS") explains them, so the tooltip wording matches
 * what the model was previously saying inline.
 */
export interface GlossaryEntry {
  /** Stable concept id — the unit of "first occurrence": the first mention of ANY alias decorates, later ones don't. */
  id: string
  /** Human label used for the tooltip heading. */
  label: string
  /** Surface forms matched in the digest (case-insensitive, whole-word). The longest are tried first. */
  aliases: string[]
  /** One-line definition shown in the tooltip. */
  definition: string
}

export const GLOSSARY: GlossaryEntry[] = [
  {
    id: 'spin',
    label: 'SPIN',
    aliases: ['Spcfy Identification Number', 'SPINs', 'SPIN'],
    definition:
      'Spcfy Identification Number — a stable id spcfy assigns to an organism so the same organism keeps the same SPIN across different orders in a project.'
  },
  {
    id: 'reads',
    label: 'Reads',
    aliases: ['reads', 'read'],
    definition:
      'Sequencing depth for an organism — a relative abundance signal (how much of that DNA was found), not a headcount of individuals.'
  },
  {
    id: 'goods-coverage',
    label: "Good's coverage",
    aliases: ["Good's coverage"],
    definition:
      'An estimate of how completely a sample was sequenced (C = 1 − singletons/reads); near 1.0 means little was missed.'
  },
  {
    id: 'otu',
    label: 'OTU',
    aliases: ['Operational Taxonomic Unit', 'OTUs', 'OTU'],
    definition:
      'Operational Taxonomic Unit — a cluster of near-identical DNA sequences used as a stand-in for one organism; may or may not resolve to a named species.'
  },
  {
    id: 'metabarcoding',
    label: 'Metabarcoding',
    aliases: ['metabarcoding'],
    definition:
      'A method that reads DNA from many organisms in one sample at once to identify which are present.'
  },
  {
    id: 'co1',
    label: 'CO1',
    aliases: ['CO1', 'COI'],
    definition: 'A standard animal DNA barcode gene, used by the BOLD reference database.'
  },
  {
    id: 'pollinator',
    label: 'Pollinator',
    aliases: ['pollinators', 'pollinator'],
    definition: 'A trait tag for organisms that pollinate plants, such as many bees and hoverflies.'
  },
  {
    id: 'red-listed',
    label: 'Red-listed',
    aliases: ['red-listed', 'red list', 'red-list'],
    definition:
      'Assigned an IUCN extinction-risk category; Least Concern and Data Deficient are treated as not threatened.'
  },
  {
    id: 'iucn-lc',
    label: 'Least Concern',
    aliases: ['Least Concern'],
    definition: 'IUCN category: widespread and abundant — not considered threatened.'
  },
  {
    id: 'iucn-nt',
    label: 'Near Threatened',
    aliases: ['Near Threatened'],
    definition: 'IUCN category: close to qualifying as threatened in the near future.'
  },
  {
    id: 'iucn-vu',
    label: 'Vulnerable',
    aliases: ['Vulnerable'],
    definition: 'IUCN category: threatened — high risk of extinction in the wild.'
  },
  {
    id: 'iucn-en',
    label: 'Endangered',
    aliases: ['Endangered'],
    definition: 'IUCN category: threatened — very high risk of extinction in the wild.'
  },
  {
    id: 'iucn-cr',
    label: 'Critically Endangered',
    aliases: ['Critically Endangered'],
    definition: 'IUCN category: threatened — extremely high risk of extinction in the wild.'
  },
  {
    id: 'iucn-dd',
    label: 'Data Deficient',
    aliases: ['Data Deficient'],
    definition: 'IUCN category: too little information to assess extinction risk — not a threatened status.'
  }
]

/** Escape a literal string for safe inclusion in a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * All aliases across the glossary, paired with their owning entry, sorted LONGEST-FIRST. Longest-first
 * matters for both the alternation (so "Critically Endangered" wins over "Endangered", "Spcfy Identification
 * Number" over a bare token) and for the lookup fallback.
 */
const ALIAS_INDEX: { alias: string; entry: GlossaryEntry }[] = GLOSSARY.flatMap((entry) =>
  entry.aliases.map((alias) => ({ alias, entry }))
).sort((a, b) => b.alias.length - a.alias.length)

/** Case-insensitive exact-alias → entry map, for resolving a matched substring back to its glossary entry. */
const ALIAS_TO_ENTRY = new Map<string, GlossaryEntry>(
  ALIAS_INDEX.map(({ alias, entry }) => [alias.toLowerCase(), entry])
)

/**
 * Build the master matcher: one global, case-insensitive, whole-word alternation of every alias. A fresh
 * RegExp is returned each call because global regexes carry mutable `lastIndex` state — callers must not share.
 * `\b` word boundaries keep matches to whole words (so "reads" ≠ "red", "OTU" ≠ "OTUx"); apostrophes and
 * hyphens inside aliases (Good's coverage, red-listed) sit fine between boundaries.
 */
export function buildGlossaryRegex(): RegExp {
  const alternation = ALIAS_INDEX.map(({ alias }) => escapeRegExp(alias)).join('|')
  return new RegExp(`\\b(?:${alternation})\\b`, 'gi')
}

/** Resolve a matched surface form (any casing) back to its glossary entry, if any. */
export function lookupGlossaryEntry(matched: string): GlossaryEntry | undefined {
  return ALIAS_TO_ENTRY.get(matched.trim().toLowerCase())
}

export interface GlossaryMatch {
  index: number
  length: number
  text: string
  entry: GlossaryEntry
}

/**
 * Pure helper mirroring the remark plugin's decision logic on a single string: find the FIRST occurrence of
 * each glossary CONCEPT (keyed by `entry.id`, so the first mention of any of a concept's aliases wins and
 * later aliases are skipped), case-insensitively and on word boundaries. Used by the unit test; the plugin
 * applies the identical regex + seen-by-id rule across the digest's mdast text nodes in document order.
 */
export function firstOccurrenceMatches(text: string): GlossaryMatch[] {
  const regex = buildGlossaryRegex()
  const seen = new Set<string>()
  const out: GlossaryMatch[] = []
  let m: RegExpExecArray | null
  while ((m = regex.exec(text)) !== null) {
    const entry = lookupGlossaryEntry(m[0])
    if (!entry || seen.has(entry.id)) continue
    seen.add(entry.id)
    out.push({ index: m.index, length: m[0].length, text: m[0], entry })
  }
  return out
}
